import React, { createContext, useRef, useState, useEffect } from "react";
import { Outlet, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { api } from "../apis/configs/axiosConfigs";
import {
  FormArray,
  FormBuilder,
  // FormControl,
  Validators,
} from "react-reactive-form";
import { startStudyDataLoad } from "../store/singletons/csrDataPollingSingleton";

export const QuestionsContext = createContext(null);

export default function QuestionsProvider() {
  const { studyId } = useParams();
  const dispatch = useDispatch();
  const [questionsDefaultData, setQuestionsDefaultData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const questionsData = useRef(null);
  const [questionsDataSaved, setQuestionsDataSaved] = useState(null);
  const [formFilledData, setFormFilledData] = useState(null);
  const [wizardFormFilledData, setWizardFormFilledData] = useState(null);

  // Stable form refs – must be declared here (React hook rules: no hooks after early returns).
  // They are initialised lazily below, after questionsDefaultData is ready.
  const qFormRef = useRef(null);
  const wizardFormRef = useRef(null);
  const laySummaryWizardFormRef = useRef(null);

  useEffect(() => {
    const fetchQuestionsData = async () => {
      setLoading(true);
      setError(null);
      try {
        // TEMPORARY: Load questions data from local JSON file for development - please don't complain
        const response = await api.get("/assets/json/questions_data");
        setQuestionsDefaultData(response.data);
        questionsData.current = response.data;
        // const response = await import("../assets/json/questions_data.json");
        // setQuestionsDefaultData(response.default);
        // questionsData.current = response.default;
      } catch (err) {
        setError("Failed to load questions data");
      } finally {
        setLoading(false);
      }
    };
    fetchQuestionsData();
  }, []);

  // Kick off the shared status-polling + tables/figures pipeline for any
  // wizard page entry — including hard refreshes that land directly on
  // PK/PD/data-mapping routes. Idempotent in the singleton.
  useEffect(() => {
    if (studyId) {
      dispatch(startStudyDataLoad(studyId));
    }
  }, [dispatch, studyId]);

  // Wait for questionsDefaultData to be loaded before initializing forms
  if (loading) {
    return <div>Loading questions...</div>;
  }
  if (error) {
    return <div>{error}</div>;
  }
  if (!questionsDefaultData) {
    return null;
  }

  // Initialise forms lazily – only once per provider lifetime.
  // This keeps each form's object reference stable across re-renders so that
  // react-reactive-form's FieldControl stays subscribed to the correct controls
  // (FieldControl only re-subscribes when its name prop changes, not when the
  // parent FormGroup object is replaced).
  if (!qFormRef.current) {
    qFormRef.current = FormBuilder.group({
      qAuthorityLink: ["", Validators.required],
    });
  }

  if (!wizardFormRef.current) {
    wizardFormRef.current = FormBuilder.group({
      qStudyPhaseNames: [""],
      qParticipantsName: [""],
      qParticipantsNameOther: [""],
      qComparative: [null],
      qDoseFinding: [null],
      qDoseFindingSafety: [null],
      qTgNamesDoseFinding: new FormArray([]),
      qDoseFindingEfficacy: [null],
      qDoseFindingStatisticalEndpoint: [""],
      qTgNamesComparison: new FormArray([]),
      qParticipantNumber: [""],
      qIntendedDuration: [null, Validators.required],
      qDemPopulation: ["", Validators.required],
      qCutOffsForDemographicsTables: ["", Validators.required],
      qCutOffsForAdverseEventsTables: ["", Validators.required],
      qCutOffsForAbnormalLabTables: ["", Validators.required],
      qPrimaryEfficacy: [null],
      qEfficacyAnalytes: new FormArray([]),
      qPrimaryEfficacyComparisonNames: new FormArray([]), // TODO: rename this to qEfficacyComparisonNames
      qPrimaryEfficacyAnalytes: new FormArray([]),
      qPrimaryEfficacyPopulation: new FormArray([]),
      qEfficacyPopulations: new FormArray([]),
      qEfficacyAnalysisTypeNames: new FormArray([]),
      qPrimaryEfficacyAnalysisType: [null],
      qEfficacyAnalysisType: [null],
      qPrimaryEfficacyAnalysisTypeNames: new FormArray([]),
      qPrimaryEfficacyAnalytesComparison: new FormArray([]),
      qPrimaryEfficacyBaseline: new FormArray([]),
      qPrimaryEfficacyTimepointNames: new FormArray([]),
      qEfficacyBaseline: [""],
      qEfficacyTimepointNames: new FormArray([]),
      qPrimaryEfficacySensitivity: [null],
      qPrimaryEfficacySensitivityNames: new FormArray([]),
      qEfficacySensitivity: [null],
      qEfficacySensitivityNames: new FormArray([]),
      qSecondaryEfficacyAnalytes: new FormArray([]),
      qEfficacyKaplanMeierAnalytes: new FormArray([]),
      qTgNamesSingleArm: [""],
      qEfficacyNonComp: [null],
      qIsEfficacy: [null],
      qPrimaryEfficacyBaselineNonComp: [""],
      qPrimaryEfficacyTimepointNonComp: [""],
      // qSingleArmMultipleColumns: new FormArray([]),
      qSingleArmMultipleColumns: [""],
      qSafetyBaseline: ["", Validators.required],
      qSafetyEndpoint: ["", Validators.required],
      qCutOffs: [""],
      qSubgroups: [null],
      qSubgroupClassification: new FormArray([]),
      qSubgroupNames: FormBuilder.group([]),
      qPK: ["true", Validators.required],
      qPkStdEnabled: [null, Validators.required],
      qPkCompEnabled: [null, Validators.required],
      qPkRelBio: [null, Validators.required],
      qPKmetaboliteNames: FormBuilder.group({
        ParentalProduct: [""],
        Metabolites: new FormArray([]),
      }),
      qStudyPhase: ["", Validators.required],
      qPdStdEnabled: [null, Validators.required],
      qPdCompEnabled: [null, Validators.required],
      qPdComparative: [null, Validators.required],
      // Lay Summary specific fields
      qShortTgName: [""],
      qStudyTitle: [""],
      qTgNames: new FormArray([
        FormBuilder.group({
          name: [""],
          role: [""],
        }),
      ]),
      qStudyPurpose: [""],
      qFollowUp: [""],
      qFollowUpDesc: [""],
      qReferences: [""],
    });
  }

  if (!laySummaryWizardFormRef.current) {
    laySummaryWizardFormRef.current = FormBuilder.group({
      // Lay Summary specific fields
      qShortTgName: ["", Validators.required],
      qStudyTitle: ["", Validators.required],
      qTgNames: new FormArray([
        FormBuilder.group({
          name: ["", Validators.required],
          role: ["", Validators.required],
        }),
      ]),
      qStudyPurpose: ["", Validators.required],
      qComparative: ["", Validators.required],
      qFollowUp: ["", Validators.required],
      qFollowUpDesc: [""],
      qReferences: new FormArray(
        [FormBuilder.control("")],
        Validators.required
      ),
    });
  }

  const qForm = qFormRef.current;
  const wizardForm = wizardFormRef.current;
  const laySummaryWizardForm = laySummaryWizardFormRef.current;

  const updateTgNamesComparisonAdditionalData = () => {
    let control;
    let qTgNamesComparisonAdditionalData = { Treatment: [], Comparator: [] };
    if (wizardForm.controls.qDoseFindingSafety.value === "true") {
      control = wizardForm.controls.qTgNamesDoseFinding;
    } else {
      control = wizardForm.controls.qTgNames;
    }
    const list = control.value
      .map(v => v["name"])
      .filter(v => v !== "")
      .map(v => ({ title: v, value: v }));

    qTgNamesComparisonAdditionalData = {
      Treatment: list,
      Comparator: list,
    };

    return qTgNamesComparisonAdditionalData;
  };

  const updatePdTreatmentGroupsComparisonAdditionalData = (suffix = "") => {
    // ComparativePDQuestions uses qPdTreatmentGroupsComp* controls
    const controlName = `qPdTreatmentGroupsComp${suffix}`;
    let qPdTreatmentGroupsComparisonAdditionalData = {
      Treatment: [],
      Comparator: [],
    };

    if (!wizardForm.contains(controlName)) {
      return qPdTreatmentGroupsComparisonAdditionalData;
    }

    const control = wizardForm.controls[controlName];
    if (!control || !control.value) {
      return qPdTreatmentGroupsComparisonAdditionalData;
    }

    const list = control.value
      .map(v => v["valueHelper"])
      .filter(v => v !== "")
      .map(v => ({ title: v, value: v }));

    qPdTreatmentGroupsComparisonAdditionalData = {
      Treatment: list,
      Comparator: list,
    };

    return qPdTreatmentGroupsComparisonAdditionalData;
  };

  const filterSubGroups = arr => {
    return arr.filter((v, i) => v.valueHelper !== "" && arr.indexOf(v) === i);
  };

  const qSubgroupClassificationFinalGroups = () => {
    let qSubgroupClassificationFinalGroupsData = [];
    qSubgroupClassificationFinalGroupsData = filterSubGroups(
      wizardForm.controls.qSubgroupClassification.value
    );

    const qSubgroupNames = wizardForm.controls.qSubgroupNames;

    Object.keys(qSubgroupNames.controls).forEach(controlName => {
      if (
        qSubgroupClassificationFinalGroupsData
          .map(e => e.valueHelper)
          .indexOf(controlName) < 0
      )
        qSubgroupNames.removeControl(controlName);
    });

    return qSubgroupClassificationFinalGroupsData;
  };

  return (
    <QuestionsContext.Provider
      value={{
        studyId,
        questionsData,
        questionsDefaultData,
        questionsDataSaved,
        setQuestionsDataSaved,
        qForm,
        wizardForm,
        laySummaryWizardForm,
        updateTgNamesComparisonAdditionalData,
        updatePdTreatmentGroupsComparisonAdditionalData,
        qSubgroupClassificationFinalGroups,
        formFilledData,
        setFormFilledData,
        wizardFormFilledData,
        setWizardFormFilledData,
      }}>
      <Outlet />
    </QuestionsContext.Provider>
  );
}
